import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e?.target?.name]: e?.target?.value });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitMessage(t('contact.successMessage'));
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSubmitMessage(null), 5000);
    }, 1000);
  };

  const contactMethods = [
    {
      icon: 'Mail',
      title: t('contact.emailUs'),
      description: t('contact.emailDesc'),
      link: 'mailto:contact@blindrecruit.com'
    },
    {
      icon: 'MessageSquare',
      title: t('contact.liveChat'),
      description: t('contact.liveChatDesc'),
      link: '#'
    },
    {
      icon: 'Phone',
      title: t('contact.callUs'),
      description: t('contact.callUsDesc'),
      link: 'tel:+15145626813'
    }
  ];

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-xl border border-white/20 shadow-lg rounded-full mb-4 md:mb-6">
            <Icon name="MessageCircle" size={20} color="#0025cc" />
            <span className="text-sm md:text-base font-medium text-[#0025cc]">{t('contact.badge')}</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#111827] mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-base md:text-lg text-[#6B7280] max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {contactMethods?.map((method, index) => (
            <a
              key={index}
              href={method?.link}
              className="bg-white/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0025cc] to-[#4F46E5] flex items-center justify-center mx-auto mb-4">
                <Icon name={method?.icon} size={24} color="#FFFFFF" />
              </div>
              <h3 className="text-lg font-semibold text-[#111827] mb-2">{method?.title}</h3>
              <p className="text-sm text-[#6B7280]">{method?.description}</p>
            </a>
          ))}
        </div>

        <div className="max-w-3xl mx-auto bg-white/40 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-12 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#111827] mb-2">
                  {t('contact.yourName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData?.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0025cc] focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#111827] mb-2">
                  {t('contact.emailAddress')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0025cc] focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-[#111827] mb-2">
                {t('contact.subject')}
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData?.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0025cc] focus:border-transparent"
                placeholder={t('contact.subjectPlaceholder')}
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-[#111827] mb-2">
                {t('contact.message')}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData?.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0025cc] focus:border-transparent resize-none"
                placeholder={t('contact.messagePlaceholder')}
              />
            </div>
            {submitMessage && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                <Icon name="CheckCircle" size={20} color="#10B981" />
                <p className="text-sm text-green-800">{submitMessage}</p>
              </div>
            )}
            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isSubmitting}
              iconName="Send"
              iconPosition="right"
            >
              {t('contact.sendMessage')}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;